import { edmCampaign } from '@bingads-webui/edm-campaign';
import type { ITopic, ITreeTopic } from './topics-selector';

interface ITopicData {
  Id: number;
  Name: string;
  ParentId: number | null;
  ParentName: string;
}

export const transformTopics = (allTopicsData: ITopicData[], selectedTopics: ITopic[]): { allTopics: ITreeTopic[], fetchedSelectedTopics: ITreeTopic[] } => {
  const allTopics: ITreeTopic[] = [];
  const fetchedSelectedTopics: ITreeTopic[] = [];

  allTopicsData.forEach((topic) => {
    const { Id, Name, ParentId } = topic;
    const isSelected = selectedTopics.some(selectedTopic => selectedTopic.TopicId === Id);

    const topicObject = {
      value: String(Id),
      content: Name,
      ...(ParentId ? { parentValue: String(ParentId) } : {}),
    };

    if (isSelected) {
      fetchedSelectedTopics.push(topicObject);
    }

    allTopics.push(topicObject);
  });

  return { allTopics, fetchedSelectedTopics };
};

export const loadTopics = (cid, aid): () => Promise<ITopicData[]> => async () => {
  const edm = edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .Topics;

  const res = await edm.get().toPromise();
  if (res && res.value && res.value.length > 0) {
    return res.value.map(topic => ({
      Id: topic.TopicId,
      Name: topic.TopicName,
      ParentId: topic.ParentId,
    }));
  }
  return [];
};

export const fetchTopics = async (selectedTopics: ITopic[], aid, cid): Promise<{ allTopics: ITreeTopic[], fetchedSelectedTopics: ITreeTopic[] }> => {
  const topicsLoader = loadTopics(cid, aid);
  const allTopicsData = await topicsLoader();
  const { allTopics, fetchedSelectedTopics } = transformTopics(allTopicsData, selectedTopics);

  return { allTopics, fetchedSelectedTopics };
};

/**
 * A helper function that adds `item` and all its ancestors to `matchedItems`.
 *
 * @param {ITreeTopic} item A single topic item.
 * @param {Map<string>} mapLookup A map for looking up topics by their value.
 * @param {Set<ITreeTopic>} matchedItems A set to collect matched topics and their ancestors.
 */
function addAncestors<T extends ITreeTopic>(
  item: ITreeTopic,
  mapLookup: Map<string, T>,
  matchedItems: Set<ITreeTopic>
): void {
  matchedItems.add(item);

  // Climb up the chain while there's a valid parent
  let parentVal = item.parentValue;
  while (parentVal) {
    const parent = mapLookup.get(parentVal);
    if (!parent) break;
    matchedItems.add(parent);
    parentVal = parent.parentValue;
  }
}

/**
 * Search a flat array of items by `searchTerm`, preserving
 * ancestor nodes (via parentValue) for any matched item.
 *
 * @param {ITreeTopic[]} topics The flat array of topics.
 * @param {string} searchTerm The user-entered search string.
 * @returns {ITreeTopic[]} A new filtered array that includes matching items + ancestors.
 */
export function searchFlatItems(
  topics: ITreeTopic[],
  searchTerm: string
): ITreeTopic[] {
  const trimmed = searchTerm.trim().toLowerCase();
  if (!trimmed) {
    return topics;
  }

  const mapLookup = new Map<string, ITreeTopic>();
  topics.forEach((topic) => {
    mapLookup.set(topic.value, topic);
  });

  const matchedItems = new Set<ITreeTopic>();

  topics.forEach((item) => {
    const itemName = (item.content).toLowerCase();
    if (itemName.includes(trimmed)) {
      addAncestors(item, mapLookup, matchedItems);
    }
  });

  return topics.filter(item => matchedItems.has(item));
}
